/**
 * LookupPanel — CRM record lookup panel shown alongside the chat thread.
 *
 * When a conversation is active, this panel searches for CRM records matching
 * the contact's phone number. It allows viewing fields, editing inline, and
 * creating new records if no match is found.
 *
 * Layout: Split into two resizable sections — Fields (top ~70%) and Notes (bottom ~30%).
 * The divider between them is draggable.
 */
import { useEffect, useCallback, useState, useRef } from 'react';
import {
  ExternalLink,
  Search,
  UserPlus,
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw,
  Send,
  StickyNote,
  MoreVertical,
  Settings,
  EyeOff,
  ChevronLeft,
  GripVertical,
  CheckSquare,
  Square,
  HelpCircle,
  Ticket,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPhoneNumber } from '@/lib/utils/format';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useLookupStore } from '@/stores/lookup-store';
import { useModuleStore } from '@/stores/module-store';
import { useUIStore } from '@/stores/ui-store';
import { useAppStore } from '@/stores/app-store';
import { getSaaSCategory } from '@/config/constants';
import { LookupField, CreateField } from './lookup-field';
import type { Conversation } from '@/types/messaging';
import type { CRMRecord, CRMNote } from '@/types/saas';

/** Renders the SaaS icon with a hidden fallback for broken images */
export function SaasIconWithFallback({ icon, name, size = 'md' }: { icon?: string; name: string; size?: 'sm' | 'md' | 'lg' }) {
  const dims = size === 'sm' ? 'h-3.5 w-3.5' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';
  const textSize = size === 'sm' ? 'text-[8px]' : size === 'lg' ? 'text-xs' : 'text-[10px]';

  return (
    <>
      {icon && (
        <img
          src={icon}
          alt={name}
          className={cn(dims, 'object-contain shrink-0')}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'flex';
          }}
        />
      )}
      <span
        className={cn(dims, textSize, 'rounded bg-primary/10 items-center justify-center font-bold text-primary shrink-0')}
        style={{ display: icon ? 'none' : 'flex' }}
      >
        {name.charAt(0)}
      </span>
    </>
  );
}

interface LookupPanelProps {
  conversation: Conversation;
}

/** True for a CRM's ticket module. Helpdesk apps (Zoho Desk, Freshdesk,
 *  Zendesk) expose tickets, and "Create a ticket" reads better than the
 *  generic "Create Record" / "Create in Tickets". */
export function isTicketModule(moduleValue: string): boolean {
  return moduleValue.toLowerCase() === 'tickets';
}

/** Label for a create-record affordance — ticket-aware. */
export function getCreateLabel(mod: { value: string; label: string }): string {
  return isTicketModule(mod.value) ? 'Create a ticket' : `Create in ${mod.label}`;
}

/** Fields to skip when displaying record data (system/internal fields) */
const SKIP_FIELDS = new Set([
  '$field_states', '$approval', '$canvas_id', '$review_process', '$followers',
  '$sharing_permission', '$approval_state', '$pathfinder', '$in_merge',
  '$review', '$orchestration', '$zia_owner_assignment', '$zia_visions',
  '$currency_symbol', '$converted', '$process_flow', '$editable',
  '$converted_detail', '$data_source_details',
]);

export function LookupPanel({ conversation }: LookupPanelProps) {
  const saasModule = useModuleStore((s) => s.saasModule);
  const saasAuthenticated = useModuleStore((s) => s.saasAuthenticated);
  const saasAuthChecked = useModuleStore((s) => s.saasAuthChecked);
  const saasAuthChecking = useModuleStore((s) => s.saasAuthChecking);
  const checkSaaSAuth = useModuleStore((s) => s.checkSaaSAuth);
  const initiateSaaSAuth = useModuleStore((s) => s.initiateSaaSAuth);
  const toggleLookupPanel = useUIStore((s) => s.toggleLookupPanel);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const saasCategory = getSaaSCategory(appCodeSAAS);

  // If saasModule is loaded but auth hasn't been checked yet, trigger the check.
  // This handles the race condition where checkSaaSAuth runs before saasModule
  // finishes loading in the background (in CONVERSATIONS view).
  useEffect(() => {
    if (saasModule && !saasAuthChecked && !saasAuthChecking) {
      checkSaaSAuth();
    }
  }, [saasModule, saasAuthChecked, saasAuthChecking, checkSaaSAuth]);

  const {
    record,
    records,
    fields,
    loading,
    error,
    editingField,
    editValues,
    saving,
    createMode,
    createModule,
    createFields,
    notes,
    notesLoading,
    notesAdding,
    currentPhone,
    lookupByPhone,
    searchLookup,
    selectRecord,
    startEditing,
    cancelEditing,
    setEditValue,
    saveField,
    enterCreateMode,
    exitCreateMode,
    createRecord,
    fetchNotes,
    addNote,
    settingsOpen,
    enabledModules,
    visibleFields,
    fieldOrder,
    settingsModule,
    settingsFields,
    settingsFieldsLoading,
    showByDefault,
    lookupSettingsLoaded,
    openSettings,
    closeSettings,
    toggleModule,
    selectSettingsModule,
    toggleFieldVisibility,
    setAllFieldsVisibility,
    reorderField,
    setShowByDefault,
    loadLookupSettings,
    reset,
  } = useLookupStore();

  // Load lookup settings from Firestore once when authenticated
  useEffect(() => {
    if (saasAuthenticated && !lookupSettingsLoaded) {
      loadLookupSettings();
    }
  }, [saasAuthenticated, lookupSettingsLoaded, loadLookupSettings]);

  const setLookupPanelOpen = useUIStore((s) => s.setLookupPanelOpen);

  // Auto-open the panel when "show by default" is enabled and settings have loaded
  useEffect(() => {
    if (lookupSettingsLoaded && showByDefault) {
      setLookupPanelOpen(true);
    }
  }, [lookupSettingsLoaded, showByDefault, setLookupPanelOpen]);

  // Look up the contact's phone when the conversation changes (only if SaaS is authenticated)
  useEffect(() => {
    if (!saasAuthenticated) {
      reset();
      return;
    }
    if (conversation.contactPhone) {
      lookupByPhone(conversation.contactPhone);
    } else {
      reset();
    }
  }, [conversation.contactPhone, saasAuthenticated, lookupByPhone, reset]);

  const handleRefresh = useCallback(() => {
    if (conversation.contactPhone) {
      // Force re-lookup by resetting current phone
      useLookupStore.setState({ currentPhone: null });
      lookupByPhone(conversation.contactPhone);
    }
  }, [conversation.contactPhone, lookupByPhone]);

  const [draftQuery, setDraftQuery] = useState(conversation.contactPhone || '');
  const [draftForPhone, setDraftForPhone] = useState(conversation.contactPhone);
  if (draftForPhone !== conversation.contactPhone) {
    setDraftForPhone(conversation.contactPhone);
    setDraftQuery(conversation.contactPhone || '');
  }

  const handleSearchSubmit = useCallback(() => {
    const q = draftQuery.trim();
    if (!q) return;
    if (/^\+?[\d\s\-()]{6,}$/.test(q)) {
      useLookupStore.setState({ currentPhone: null });
      lookupByPhone(q);
    } else {
      searchLookup(q);
    }
  }, [draftQuery, lookupByPhone, searchLookup]);

  const lookupTerm = currentPhone || conversation.contactPhone;

  const handleCreateSubmit = useCallback(async () => {
    if (!createModule) return;
    await createRecord(createModule, editValues);
  }, [createModule, editValues, createRecord]);

  // Get the CRM web URL for the active record
  const recordWebUrl = record && saasModule
    ? saasModule.getRecordWebURL(record.module, record.id)
    : '';

  // Get supported modules for the "Create" option
  const supportedModules = saasModule?.getSupportedModules() || [];

  // Only enabled modules for create buttons
  const enabledModulesList = supportedModules.filter(
    (mod) => mod.value in enabledModules ? enabledModules[mod.value] !== false : mod.selected
  );

  // Show the split layout only when viewing a record
  const showSplitLayout = saasAuthenticated && !loading && !error && !createMode && record;

  return (
    <div data-testid="lookup-panel" className="w-80 shrink-0 border-l border-border bg-card flex flex-col h-full min-h-0 overflow-hidden">
      {/* Panel Header — no bottom border to avoid mis-aligning with the chat
          thread header next to it; the column's left border + bg-card already
          separates this panel from the conversation. */}
      <div data-testid="lookup-panel-header" className="flex items-center gap-2 px-3 py-2.5 bg-card shrink-0">
        <SaasIconWithFallback icon={saasModule?.icon} name={saasModule?.name || saasCategory} size="md" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground truncate flex items-baseline gap-1.5">
            {createMode ? (
              createModule && isTicketModule(createModule) ? 'Create a Ticket' : 'Create Record'
            ) : (
              <>
                {/* "LookUp" wordmark — split caps treatment so it reads like a
                    small logo rather than a sentence. */}
                <span className="font-bold tracking-tight">
                  <span className="text-foreground">Look</span>
                  <span className="text-primary">Up</span>
                </span>
                <span className="text-muted-foreground/70 font-normal">·</span>
                <span className="font-semibold text-foreground/80 truncate">
                  {saasModule?.name || saasCategory}
                </span>
              </>
            )}
          </h3>
        </div>
        <HeaderMenu
          loading={loading}
          onRefresh={handleRefresh}
          onSettings={settingsOpen ? closeSettings : openSettings}
          onHide={() => toggleLookupPanel()}
        />
      </div>

      {saasAuthenticated && !settingsOpen && !createMode && (
        <div className="px-3 pb-2 flex items-center gap-2 shrink-0">
          <div className="flex-1 flex items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary/60 transition">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input
              type="text"
              value={draftQuery}
              onChange={(e) => setDraftQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
              placeholder={`Search ${saasModule?.name || saasCategory} records...`}
              className="flex-1 min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            {draftQuery && (
              <button
                type="button"
                onClick={() => setDraftQuery('')}
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            size="icon-sm"
            onClick={handleSearchSubmit}
            disabled={!draftQuery.trim() || loading}
            title="Search"
            aria-label="Search"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
      )}

      {/* Panel Body */}
      {settingsOpen ? (
        <LookupSettings
          supportedModules={supportedModules}
          enabledModules={enabledModules}
          visibleFields={visibleFields}
          fieldOrder={fieldOrder}
          settingsModule={settingsModule}
          settingsFields={settingsFields}
          settingsFieldsLoading={settingsFieldsLoading}
          showByDefault={showByDefault}
          onToggleModule={toggleModule}
          onSelectModule={selectSettingsModule}
          onToggleField={toggleFieldVisibility}
          onSetAllFieldsVisibility={setAllFieldsVisibility}
          onReorderField={reorderField}
          onSetShowByDefault={setShowByDefault}
          onBack={closeSettings}
        />
      ) : showSplitLayout ? (
        <SplitFieldsAndNotes
          record={record}
          fields={fields}
          editingField={editingField}
          editValues={editValues}
          saving={saving}
          notes={notes}
          notesLoading={notesLoading}
          notesAdding={notesAdding}
          recordWebUrl={recordWebUrl}
          onStartEdit={startEditing}
          onCancelEdit={cancelEditing}
          onChange={setEditValue}
          onSave={saveField}
          onFetchNotes={fetchNotes}
          onAddNote={addNote}
        />
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          {/* Auth required state */}
          {!saasAuthenticated && (
            <div data-testid="lookup-auth-required" className="flex flex-col items-center justify-center h-full p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 mb-3">
                <SaasIconWithFallback icon={saasModule?.icon} name={saasModule?.name || saasCategory} size="lg" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">{saasCategory} Authorization Required</p>
              <p className="text-xs text-muted-foreground mb-4">
                Authorize your {saasModule?.name || saasCategory} account to look up contact records.
              </p>
              <Button data-testid="lookup-auth-btn" variant="default" size="sm" onClick={initiateSaaSAuth}>
                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                Authorize {saasModule?.name || saasCategory}
              </Button>
            </div>
          )}

          {saasAuthenticated && loading && <LoadingSkeleton />}

          {saasAuthenticated && !loading && error && (
            <ErrorState error={error} onRetry={handleRefresh} />
          )}

          {saasAuthenticated && !loading && !error && createMode && (
            <CreateRecordView
              fields={createFields}
              values={editValues}
              saving={saving}
              onChange={setEditValue}
              onSubmit={handleCreateSubmit}
              onCancel={exitCreateMode}
              visibleFieldsConfig={createModule ? visibleFields[createModule] : undefined}
              contactPhone={conversation.contactPhone}
              module={createModule}
            />
          )}

          {saasAuthenticated && !loading && !error && !createMode && !record && records.length === 0 && !lookupTerm && (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-3">
                <Search className="h-6 w-6 text-muted-foreground/40" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">Open a conversation</p>
              <p className="text-xs text-muted-foreground max-w-[220px]">
                Select a conversation with a phone number to look it up in {saasModule?.name || saasCategory}.
              </p>
            </div>
          )}

          {saasAuthenticated && !loading && !error && !createMode && !record && records.length === 0 && lookupTerm && (
            <NoMatchState
              phone={lookupTerm}
              saasName={saasModule?.name || saasCategory}
              modules={enabledModulesList}
              onCreateIn={enterCreateMode}
              onConfigureModules={openSettings}
            />
          )}

          {saasAuthenticated && !loading && !error && !createMode && !record && records.length > 0 && (
            <MultiMatchState
              records={records}
              onSelect={selectRecord}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Toggle Switch ────────────────────────────────────────────────────────

/** Small inline toggle switch component */
function ToggleSwitch({
  checked,
  onChange,
  size = 'md',
}: {
  checked: boolean;
  onChange: () => void;
  size?: 'sm' | 'md';
}) {
  const dims = size === 'sm'
    ? { track: 'w-7 h-4', knob: 'h-3 w-3', translate: 'translate-x-3' }
    : { track: 'w-8 h-[18px]', knob: 'h-3.5 w-3.5', translate: 'translate-x-[14px]' };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      className={cn(
        'relative inline-flex shrink-0 rounded-full transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
        dims.track,
        checked ? 'bg-primary' : 'bg-muted-foreground/25'
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ease-in-out',
          dims.knob,
          'mt-[2px] ml-[2px]',
          checked ? dims.translate : 'translate-x-0'
        )}
      />
    </button>
  );
}

// ─── Settings View ────────────────────────────────────────────────────────

export function LookupSettings({
  supportedModules,
  enabledModules,
  visibleFields,
  fieldOrder,
  settingsModule,
  settingsFields,
  settingsFieldsLoading,
  showByDefault,
  onToggleModule,
  onSelectModule,
  onToggleField,
  onSetAllFieldsVisibility,
  onReorderField,
  onSetShowByDefault,
  onBack,
}: {
  supportedModules: Array<{ value: string; label: string }>;
  enabledModules: Record<string, boolean>;
  visibleFields: Record<string, Record<string, boolean>>;
  fieldOrder: Record<string, string[]>;
  settingsModule: string | null;
  settingsFields: import('@/types/saas').CRMField[];
  settingsFieldsLoading: boolean;
  showByDefault: boolean;
  onToggleModule: (module: string) => void;
  onSelectModule: (module: string) => Promise<void>;
  onToggleField: (module: string, fieldApiName: string) => void;
  onSetAllFieldsVisibility: (module: string, visible: boolean, fieldApiNames: string[]) => void;
  onReorderField: (module: string, fromIndex: number, toIndex: number, fieldApiNames: string[]) => void;
  onSetShowByDefault: (value: boolean) => void;
  onBack: () => void;
}) {
  // Drag state for field reordering
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Search filter for fields
  const [fieldSearch, setFieldSearch] = useState('');

  // If a module is selected, show field config
  if (settingsModule) {
    const rawModuleFields = settingsFields
      .filter((f) => !SKIP_FIELDS.has(f.apiName) && !f.apiName.startsWith('$'))
      // Sort alphabetically by displayLabel (ascending)
      .sort((a, b) => (a.displayLabel || '').localeCompare(b.displayLabel || ''));

    // Apply saved field order on top of alphabetical default
    const savedOrder = fieldOrder[settingsModule] || [];
    const sortedFields = savedOrder.length > 0
      ? [
          ...savedOrder
            .map((apiName) => rawModuleFields.find((f) => f.apiName === apiName))
            .filter(Boolean) as typeof rawModuleFields,
          ...rawModuleFields.filter((f) => !savedOrder.includes(f.apiName)),
        ]
      : rawModuleFields;

    const fieldConfig = visibleFields[settingsModule] || {};
    const allFieldNames = sortedFields.map((f) => f.apiName);
    const visibleCount = sortedFields.filter((f) => fieldConfig[f.apiName] !== false).length;
    const allVisible = visibleCount === sortedFields.length;

    // Apply search filter
    const searchLower = fieldSearch.toLowerCase().trim();
    const moduleFields = searchLower
      ? sortedFields.filter(
          (f) =>
            f.displayLabel.toLowerCase().includes(searchLower) ||
            f.apiName.toLowerCase().includes(searchLower) ||
            f.dataType.toLowerCase().includes(searchLower)
        )
      : sortedFields;

    const handleDragStart = (index: number) => {
      setDragIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
      e.preventDefault();
      setDragOverIndex(index);
    };

    const handleDrop = (toIndex: number) => {
      if (dragIndex !== null && dragIndex !== toIndex) {
        onReorderField(settingsModule, dragIndex, toIndex, allFieldNames);
      }
      setDragIndex(null);
      setDragOverIndex(null);
    };

    const handleDragEnd = () => {
      setDragIndex(null);
      setDragOverIndex(null);
    };

    return (
      <div data-testid="lookup-field-config" className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Back header */}
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border/50">
          <button
            onClick={() => { onSelectModule(''); setFieldSearch(''); }}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold text-foreground">{settingsModule}</span>
          </div>
        </div>

        {/* Descriptive text */}
        <div className="shrink-0 px-3 pt-2.5 pb-1.5">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Choose which fields to show for <span className="font-medium text-foreground">{settingsModule}</span> records. Drag to reorder how they appear.
          </p>
        </div>

        {/* Search input */}
        {!settingsFieldsLoading && sortedFields.length > 0 && (
          <div className="shrink-0 px-3 pb-1.5">
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1">
              <Search className="h-3 w-3 text-muted-foreground shrink-0" />
              <input
                type="text"
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                placeholder="Search fields..."
                className="flex-1 min-w-0 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              {fieldSearch && (
                <button
                  onClick={() => setFieldSearch('')}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span className="text-xs">✕</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Select all / Deselect all bar */}
        {!settingsFieldsLoading && sortedFields.length > 0 && (
          <div className="shrink-0 flex items-center justify-between px-3 py-1.5 border-b border-border/30">
            <span className="text-[10px] text-muted-foreground">
              {visibleCount} of {sortedFields.length} visible
              {searchLower && ` · ${moduleFields.length} matching`}
            </span>
            <button
              onClick={() => onSetAllFieldsVisibility(settingsModule, !allVisible, allFieldNames)}
              className="flex items-center gap-1 text-[10px] font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {allVisible ? (
                <>
                  <Square className="h-3 w-3" />
                  Deselect all
                </>
              ) : (
                <>
                  <CheckSquare className="h-3 w-3" />
                  Select all
                </>
              )}
            </button>
          </div>
        )}

        {/* Field list */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          {settingsFieldsLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full rounded-lg" />
              ))}
            </div>
          ) : moduleFields.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-6 text-center">
              <p className="text-xs text-muted-foreground">
                {searchLower ? 'No fields matching your search.' : 'No configurable fields available for this module.'}
              </p>
            </div>
          ) : (
            <div className="p-1.5">
              {moduleFields.map((field, index) => {
                const isVisible = fieldConfig[field.apiName] !== false;
                const isDragging = dragIndex === index;
                const isDragOver = dragOverIndex === index;
                const isSearching = !!searchLower;

                return (
                  <div
                    key={field.apiName}
                    draggable={!isSearching}
                    onDragStart={!isSearching ? () => handleDragStart(index) : undefined}
                    onDragOver={!isSearching ? (e) => handleDragOver(e, index) : undefined}
                    onDrop={!isSearching ? () => handleDrop(index) : undefined}
                    onDragEnd={!isSearching ? handleDragEnd : undefined}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all group',
                      isDragging && 'opacity-40 scale-[0.98]',
                      isDragOver && !isDragging && 'border-t-2 border-primary/40',
                      !isDragging && 'hover:bg-muted/50'
                    )}
                  >
                    {/* Drag handle — hidden when searching */}
                    {!isSearching && (
                      <div className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors">
                        <GripVertical className="h-3 w-3" />
                      </div>
                    )}

                    {/* Toggle */}
                    <ToggleSwitch
                      checked={isVisible}
                      onChange={() => onToggleField(settingsModule, field.apiName)}
                      size="sm"
                    />

                    {/* Field info */}
                    <div className="flex-1 min-w-0 flex items-center gap-1.5">
                      <span className={cn(
                        'text-xs truncate transition-colors',
                        isVisible ? 'text-foreground' : 'text-muted-foreground'
                      )}>
                        {field.displayLabel}
                      </span>
                      {field.required && (
                        <span className="shrink-0 text-[9px] text-amber-500 font-bold">*</span>
                      )}
                    </div>

                    {/* Data type badge */}
                    <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                      {field.dataType}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Module list view
  return (
    <div data-testid="lookup-settings" className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Settings header */}
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border/50">
        <button
          type="button"
          onClick={onBack}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Back"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <Settings className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-foreground">Lookup Settings</span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        {/* Descriptive text */}
        <div className="px-3 pt-3 pb-2">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Select which CRM modules to search when looking up contacts. Tap a module to configure its visible fields.
          </p>
        </div>

        {/* Show by default preference */}
        <div className="px-2 pb-2">
          <div
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-muted/60 transition-colors cursor-pointer"
            onClick={() => onSetShowByDefault(!showByDefault)}
          >
            <ToggleSwitch
              checked={showByDefault}
              onChange={() => onSetShowByDefault(!showByDefault)}
            />
            <div className="flex-1 min-w-0">
              <span className={cn(
                'text-xs font-medium transition-colors',
                showByDefault ? 'text-foreground' : 'text-muted-foreground'
              )}>
                Show panel by default
              </span>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Auto-open lookup panel in conversations
              </p>
            </div>
          </div>
        </div>

        {/* Modules section */}
        <div className="px-2 pb-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5 mb-0.5">
            Modules
          </p>
          <div className="space-y-0.5">
            {supportedModules.map((mod) => {
              const isEnabled = mod.value in enabledModules ? enabledModules[mod.value] !== false : mod.selected;
              const modFieldConfig = visibleFields[mod.value];
              const hasFieldConfig = modFieldConfig && Object.keys(modFieldConfig).length > 0;

              return (
                <div
                  key={mod.value}
                  className={cn(
                    'flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors',
                    'hover:bg-muted/60'
                  )}
                >
                  {/* Toggle */}
                  <ToggleSwitch
                    checked={isEnabled}
                    onChange={() => onToggleModule(mod.value)}
                  />

                  {/* Module name */}
                  <div className="flex-1 min-w-0">
                    <span className={cn(
                      'text-xs font-medium transition-colors',
                      isEnabled ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                      {mod.label}
                    </span>
                    {isEnabled && hasFieldConfig && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {Object.values(modFieldConfig).filter((v) => v !== false).length} fields configured
                      </p>
                    )}
                  </div>

                  {/* Configure fields button */}
                  {isEnabled && (
                    <button
                      onClick={() => onSelectModule(mod.value)}
                      className={cn(
                        'flex items-center gap-1 shrink-0 text-[10px] font-medium px-2 py-1 rounded-md transition-colors',
                        'text-primary bg-primary/5 hover:bg-primary/10'
                      )}
                    >
                      Fields
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Header Menu ──────────────────────────────────────────────────────────

function HeaderMenu({
  loading,
  onRefresh,
  onSettings,
  onHide,
}: {
  loading: boolean;
  onRefresh: () => void;
  onSettings: () => void;
  onHide: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={menuRef} className="relative shrink-0">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="More options"
      >
        <MoreVertical className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] rounded-lg border border-border bg-popover p-1 shadow-lg animate-in fade-in-0 zoom-in-95">
          <button
            onClick={() => { onRefresh(); setOpen(false); }}
            disabled={loading}
            className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs text-popover-foreground rounded-md hover:bg-muted transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            onClick={() => { onSettings(); setOpen(false); }}
            className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs text-popover-foreground rounded-md hover:bg-muted transition-colors"
          >
            <Settings className="h-3 w-3" />
            Settings
          </button>
          <div className="my-1 h-px bg-border" />
          <button
            onClick={() => { onHide(); setOpen(false); }}
            className="flex items-center gap-2 w-full px-2.5 py-1.5 text-xs text-popover-foreground rounded-md hover:bg-muted transition-colors"
          >
            <EyeOff className="h-3 w-3" />
            Hide panel
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Split Layout ─────────────────────────────────────────────────────────

/** Resizable split: Fields (top) + Notes (bottom) with draggable divider */
function SplitFieldsAndNotes({
  record,
  fields,
  editingField,
  editValues,
  saving,
  notes,
  notesLoading,
  notesAdding,
  recordWebUrl,
  onStartEdit,
  onCancelEdit,
  onChange,
  onSave,
  onFetchNotes,
  onAddNote,
}: {
  record: CRMRecord;
  fields: import('@/types/saas').CRMField[];
  editingField: string | null;
  editValues: Record<string, unknown>;
  saving: boolean;
  notes: CRMNote[];
  notesLoading: boolean;
  notesAdding: boolean;
  recordWebUrl: string;
  onStartEdit: (apiName: string) => void;
  onCancelEdit: () => void;
  onChange: (apiName: string, value: unknown) => void;
  onSave: (apiName: string) => Promise<boolean>;
  onFetchNotes: () => Promise<void>;
  onAddNote: (content: string) => Promise<boolean>;
}) {
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  // Zoho Recruit has no notes API surface we support — hide the Notes panel.
  const notesSupported = appCodeSAAS !== 'zohorecruit';
  const containerRef = useRef<HTMLDivElement>(null);
  // Split ratio: percentage of container height for the fields section (top)
  const [splitPercent, setSplitPercent] = useState(70);
  const isDragging = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const y = moveEvent.clientY - rect.top;
      const percent = Math.min(Math.max((y / rect.height) * 100, 20), 85);
      setSplitPercent(percent);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  return (
    <div ref={containerRef} className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Fields section (top) */}
      <div
        className="overflow-y-auto scrollbar-thin"
        style={{ height: notesSupported ? `${splitPercent}%` : '100%' }}
      >
        <RecordView
          record={record}
          fields={fields}
          editingField={editingField}
          editValues={editValues}
          saving={saving}
          recordWebUrl={recordWebUrl}
          onStartEdit={onStartEdit}
          onCancelEdit={onCancelEdit}
          onChange={onChange}
          onSave={onSave}
        />
      </div>

      {notesSupported && (
        <>
          {/* Draggable divider */}
          <div
            className="shrink-0 flex items-center justify-center h-1.5 cursor-row-resize group hover:bg-primary/10 active:bg-primary/15 transition-colors border-t border-border/50"
            onMouseDown={handleMouseDown}
          >
            <div className="w-8 h-0.5 rounded-full bg-border group-hover:bg-primary/30 transition-colors" />
          </div>

          {/* Notes section (bottom) */}
          <div
            className="flex flex-col min-h-0 overflow-hidden"
            style={{ height: `${100 - splitPercent}%` }}
          >
            <NotesSection
              notes={notes}
              loading={notesLoading}
              adding={notesAdding}
              onFetch={onFetchNotes}
              onAdd={onAddNote}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Notes Section ────────────────────────────────────────────────────────

export function NotesSection({
  notes,
  loading,
  adding,
  onFetch,
  onAdd,
}: {
  notes: CRMNote[];
  loading: boolean;
  adding: boolean;
  onFetch: () => Promise<void>;
  onAdd: (content: string) => Promise<boolean>;
}) {
  const [newNote, setNewNote] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(async () => {
    const trimmed = newNote.trim();
    if (!trimmed || adding) return;
    const success = await onAdd(trimmed);
    if (success) {
      setNewNote('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  }, [newNote, adding, onAdd]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // Auto-resize textarea
  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewNote(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 80) + 'px';
  }, []);

  return (
    <div data-testid="lookup-notes-section" className="flex flex-col h-full min-h-0 bg-amber-50/60 dark:bg-amber-950/20">
      {/* Compact header bar */}
      <div className="shrink-0 flex items-center justify-between px-3 py-1.5 bg-amber-100/60 dark:bg-amber-900/20">
        <div className="flex items-center gap-1.5">
          <StickyNote className="h-3 w-3 text-amber-500 dark:text-amber-400" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">
            Notes
          </span>
          {notes.length > 0 && (
            <span className="text-[9px] font-medium tabular-nums text-amber-600 dark:text-amber-400">
              ({notes.length})
            </span>
          )}
        </div>
        <button
          onClick={onFetch}
          disabled={loading}
          className="p-0.5 rounded transition-colors text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300"
          title="Refresh notes"
        >
          <RefreshCw className={cn('h-2.5 w-2.5', loading && 'animate-spin')} />
        </button>
      </div>

      {/* Add note input — pinned below header */}
      <div className="shrink-0 px-2.5 py-2 border-b border-amber-300/60 dark:border-amber-700/40">
        <div className="flex items-end gap-1.5 rounded-lg overflow-hidden transition-colors bg-background border border-amber-300/60 dark:border-amber-700/40">
          <textarea
            data-testid="lookup-note-input"
            ref={textareaRef}
            value={newNote}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder="Write a note..."
            rows={1}
            className="flex-1 min-w-0 bg-transparent px-2.5 py-1.5 text-xs text-foreground focus:outline-none resize-none overflow-hidden"
          />
          <button
            onClick={handleSubmit}
            disabled={!newNote.trim() || adding}
            className={cn(
              'shrink-0 m-1 p-1 rounded-md transition-all',
              newNote.trim() && !adding
                ? 'bg-amber-400 dark:bg-amber-600 text-amber-900 dark:text-amber-100 shadow-sm'
                : 'text-amber-600/40 dark:text-amber-400/40'
            )}
            title="Add note (Enter)"
          >
            {adding ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
          </button>
        </div>
      </div>

      {/* Notes list */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin px-2.5 py-1.5">
        {loading && notes.length === 0 ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-md p-2.5 space-y-1 bg-amber-100/40 dark:bg-amber-900/20">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-2.5 w-1/3" />
              </div>
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-[11px] text-amber-600 dark:text-amber-400">No notes yet</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {notes.map((note) => (
              <NoteItem key={note.id} note={note} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NoteItem({ note }: { note: CRMNote }) {
  const timeStr = note.createdAt
    ? formatNoteTime(note.createdAt)
    : '';

  return (
    <div className="rounded-md px-2.5 py-2 transition-colors bg-amber-100/40 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/30">
      <p className="text-xs whitespace-pre-wrap break-words leading-relaxed line-clamp-4 text-amber-950 dark:text-amber-100">
        {note.content}
      </p>
      {(timeStr || note.createdBy) && (
        <p className="text-[10px] mt-1 text-amber-700/70 dark:text-amber-400/60">
          {timeStr}
          {timeStr && note.createdBy && ' \u00b7 '}
          {note.createdBy}
        </p>
      )}
    </div>
  );
}

/** Format a note timestamp for display */
function formatNoteTime(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;

    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────

export function LoadingSkeleton() {
  return (
    <div data-testid="lookup-loading" className="p-3 space-y-3">
      {/* Search indicator */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground pb-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Searching CRM records...
      </div>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="space-y-1.5 py-1.5">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div data-testid="lookup-error" className="flex flex-col items-center justify-center h-full p-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 mb-3">
        <AlertCircle className="h-6 w-6 text-destructive" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">Lookup failed</p>
      <p className="text-xs text-muted-foreground mb-4">{error}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

function NoMatchState({
  phone,
  saasName,
  modules,
  onCreateIn,
  onConfigureModules,
}: {
  phone: string;
  saasName: string;
  modules: Array<{ value: string; label: string }>;
  onCreateIn: (module: string) => Promise<void>;
  onConfigureModules: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-between h-full p-6 text-center">
      <div className="flex-1 flex flex-col items-center justify-center w-full">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-3">
          <Search className="h-6 w-6 text-muted-foreground/40" />
        </div>
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-sm font-medium text-foreground">No record found</p>
          <span
            className="inline-flex cursor-help text-muted-foreground/60 hover:text-muted-foreground"
            title="Only the modules enabled in Lookup Settings are searched. If the contact lives in a different module, use 'Configure modules to look in' below to expand the lookup scope."
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          matching {formatPhoneNumber(phone) || phone} in {saasName}
        </p>
        <div className="space-y-2 w-full max-w-[220px]">
          {modules.map((mod) => (
            <Button
              key={mod.value}
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => onCreateIn(mod.value)}
            >
              <UserPlus className="mr-2 h-3.5 w-3.5" />
              {getCreateLabel(mod)}
            </Button>
          ))}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="mt-4 text-xs text-muted-foreground hover:text-foreground"
        onClick={onConfigureModules}
      >
        <Settings className="mr-1.5 h-3.5 w-3.5" />
        Configure modules to look in
      </Button>
    </div>
  );
}

export function MultiMatchState({
  records,
  onSelect,
}: {
  records: CRMRecord[];
  onSelect: (record: CRMRecord) => Promise<void>;
}) {
  const saasModule = useModuleStore((s) => s.saasModule);
  const loadRelatedTickets = useLookupStore((s) => s.loadRelatedTickets);
  const ticketsLoadingForId = useLookupStore((s) => s.ticketsLoadingForId);
  const supportsTickets = !!saasModule?.fetchRelatedTickets;

  return (
    <div className="p-3">
      <p className="text-xs font-medium text-muted-foreground mb-2 px-1">
        {records.length} records found — select one:
      </p>
      <div className="space-y-1">
        {records.map((rec) => {
          const isContact = rec.module.toLowerCase() !== 'tickets';
          const canLoadTickets = supportsTickets && isContact;
          const loadingTickets = ticketsLoadingForId === rec.id;
          return (
            <div
              key={`${rec.module}_${rec.id}`}
              className="rounded-md hover:bg-muted transition-colors"
            >
              <button
                onClick={() => onSelect(rec)}
                className="flex items-center gap-2.5 w-full text-left px-2.5 py-2"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary shrink-0">
                  {(rec.displayName || rec.module).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {rec.displayName || `${rec.moduleLabel ?? rec.module} ${rec.id}`}
                  </p>
                  {/* flex-wrap so a long module label (e.g. a Monday board
                      name) pushes the phone number to the next line instead
                      of squeezing it; the label itself ellipsizes if it
                      alone exceeds the row width. */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="max-w-full truncate text-[10px] text-primary/70 font-medium bg-primary/5 px-1.5 py-0.5 rounded">
                      {rec.moduleLabel ?? rec.module}
                    </span>
                    {rec.phone && (
                      <span className="text-[10px] text-muted-foreground truncate">
                        {formatPhoneNumber(rec.phone)}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
              </button>
              {canLoadTickets && (
                <div className="px-2.5 pb-2 -mt-0.5">
                  <button
                    onClick={() => loadRelatedTickets(rec)}
                    disabled={loadingTickets}
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md text-primary bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-60"
                  >
                    {loadingTickets ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Ticket className="h-3 w-3" />
                    )}
                    Load tickets
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RecordView({
  record,
  fields,
  editingField,
  editValues,
  saving,
  recordWebUrl,
  onStartEdit,
  onCancelEdit,
  onChange,
  onSave,
}: {
  record: CRMRecord;
  fields: import('@/types/saas').CRMField[];
  editingField: string | null;
  editValues: Record<string, unknown>;
  saving: boolean;
  recordWebUrl: string;
  onStartEdit: (apiName: string) => void;
  onCancelEdit: () => void;
  onChange: (apiName: string, value: unknown) => void;
  onSave: (apiName: string) => Promise<boolean>;
}) {
  const isFieldVisible = useLookupStore((s) => s.isFieldVisible);
  const getFieldOrder = useLookupStore((s) => s.getFieldOrder);

  // Use field definitions for proper labels + types, then augment with any
  // populated record.data keys the field schema didn't cover. Some CRMs'
  // schema endpoints return nothing on restricted plans, leaving standard
  // fields (firstName/email/phone) absent from `fields`; without this merge
  // the actual values would never render. Nested objects and arrays-of-
  // objects are skipped — they have no flat-text display and would
  // otherwise render as "[object Object]".
  const definedApiNames = new Set(fields.map((f) => f.apiName));
  const isFlatRenderable = (v: unknown): boolean => {
    if (v == null) return true;
    if (Array.isArray(v)) return v.every((item) => item == null || typeof item !== 'object');
    return typeof v !== 'object';
  };
  const dataDerivedFields = Object.keys(record.data || {})
    .filter(
      (key) =>
        !SKIP_FIELDS.has(key) &&
        !key.startsWith('$') &&
        !definedApiNames.has(key) &&
        isFlatRenderable(record.data[key])
    )
    .map((key) => {
      const v = record.data[key];
      // Declare datetime/date/boolean dataTypes from value shape so the
      // formatter renders them properly (ISO timestamps as readable dates,
      // bools as Yes/No).
      let dataType = 'text';
      if (typeof v === 'boolean') {
        dataType = 'boolean';
      } else if (typeof v === 'string') {
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(v)) dataType = 'datetime';
        else if (/^\d{4}-\d{2}-\d{2}$/.test(v)) dataType = 'date';
      }
      return {
        apiName: key,
        displayLabel: key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2'),
        dataType,
        required: false,
        // Auto-derived fields are read-only — they exist because the schema
        // API didn't declare them, which usually means they're system /
        // internal properties (IDs, audit timestamps, foreign keys) that the
        // API won't accept on PUT anyway. Only schema-defined fields with
        // explicit readOnly:false should be editable.
        readOnly: true,
      };
    });
  const rawFields = [
    ...fields.filter((f) => !SKIP_FIELDS.has(f.apiName) && !f.apiName.startsWith('$')),
    ...dataDerivedFields,
  ].sort((a, b) => (a.displayLabel || '').localeCompare(b.displayLabel || ''));

  // Apply saved field order, then visibility filter
  const savedOrder = getFieldOrder(record.module);
  const orderedFields = savedOrder.length > 0
    ? [
        ...savedOrder
          .map((apiName) => rawFields.find((f) => f.apiName === apiName))
          .filter(Boolean) as typeof rawFields,
        ...rawFields.filter((f) => !savedOrder.includes(f.apiName)),
      ]
    : rawFields;

  const fieldsToRender = orderedFields.filter((f) => isFieldVisible(record.module, f.apiName));

  return (
    <div data-testid="lookup-record-view" className="p-2">
      {/* Record name header */}
      {record.displayName && (
        <div className="px-2 py-2 mb-1 border-b border-border/50">
          <div className="flex items-center gap-1.5">
            {recordWebUrl ? (
              <a
                href={recordWebUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 min-w-0 text-primary hover:underline"
                title="Open in CRM"
              >
                <ExternalLink className="h-3 w-3 shrink-0" />
                <span className="text-sm font-semibold truncate">{record.displayName}</span>
              </a>
            ) : (
              <p className="text-sm font-semibold text-foreground truncate">{record.displayName}</p>
            )}
            <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
              {record.moduleLabel ?? record.module}
            </span>
          </div>
          {record.email && (
            <p className="text-xs text-muted-foreground">{record.email}</p>
          )}
          {record.phone && (
            <p className="text-xs text-muted-foreground">{formatPhoneNumber(record.phone)}</p>
          )}
        </div>
      )}

      {/* Fields */}
      <div className="space-y-0.5">
        {fieldsToRender.map((field) => {
          const value = record.data[field.apiName];
          // Don't filter empty values here — LookupField hides empty
          // read-only rows and renders empty editable rows as a clickable
          // "Empty" placeholder so users can fill them in.
          return (
            <LookupField
              key={field.apiName}
              field={field}
              value={value}
              editing={editingField === field.apiName}
              saving={saving && editingField === field.apiName}
              editValue={editValues[field.apiName] ?? value ?? ''}
              onStartEdit={() => onStartEdit(field.apiName)}
              onChange={(v) => onChange(field.apiName, v)}
              onSave={() => onSave(field.apiName)}
              onCancel={onCancelEdit}
            />
          );
        })}
      </div>
    </div>
  );
}

export function CreateRecordView({
  fields,
  values,
  saving,
  onChange,
  onSubmit,
  onCancel,
  contactPhone,
  visibleFieldsConfig,
  module,
}: {
  fields: import('@/types/saas').CRMField[];
  values: Record<string, unknown>;
  saving: boolean;
  onChange: (apiName: string, value: unknown) => void;
  onSubmit: () => void;
  onCancel: () => void;
  contactPhone: string;
  visibleFieldsConfig?: Record<string, boolean>;
  module?: string | null;
}) {
  const isTicket = !!module && isTicketModule(module);
  // Show required fields first, then optional fields
  const editableFields = fields.filter(
    (f) =>
      !f.readOnly &&
      !SKIP_FIELDS.has(f.apiName) &&
      !f.apiName.startsWith('$') &&
      f.dataType !== 'lookup' &&
      f.dataType !== 'ownerlookup' &&
      f.dataType !== 'profileimage' &&
      f.dataType !== 'formula'
  );

  const hasFieldConfig = visibleFieldsConfig && Object.keys(visibleFieldsConfig).length > 0;

  // Always include mandatory fields
  const requiredFields = editableFields.filter((f) => f.required);

  let fieldsToShow: typeof editableFields;

  if (hasFieldConfig) {
    // User has configured visible fields — show mandatory + user-selected fields
    const selectedOptional = editableFields.filter(
      (f) => !f.required && visibleFieldsConfig![f.apiName] !== false
    );
    fieldsToShow = [...requiredFields, ...selectedOptional];
  } else {
    // No field config — fall back to default behaviour (mandatory + common fields)
    const optionalFields = editableFields.filter((f) => !f.required);
    const commonOptionalNames = new Set([
      'Phone', 'Mobile', 'Email', 'Last_Name', 'First_Name',
      'Company', 'Title', 'Description',
    ]);
    const priorityOptional = optionalFields.filter((f) => commonOptionalNames.has(f.apiName));
    const restOptional = optionalFields.filter((f) => !commonOptionalNames.has(f.apiName));

    fieldsToShow = [...requiredFields, ...priorityOptional];
    if (fieldsToShow.length < 10) {
      fieldsToShow.push(...restOptional.slice(0, 10 - fieldsToShow.length));
    }
  }

  return (
    <div data-testid="lookup-create-record" className="p-3">
      <p className="text-xs text-muted-foreground mb-3">
        Creating for {formatPhoneNumber(contactPhone)}
      </p>

      <div className="space-y-1">
        {fieldsToShow.map((field) => (
          <CreateField
            key={field.apiName}
            field={field}
            value={values[field.apiName] ?? ''}
            onChange={(v) => onChange(field.apiName, v)}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
        <Button
          variant="default"
          size="sm"
          onClick={onSubmit}
          disabled={saving}
          className="flex-1"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Creating...
            </>
          ) : (
            isTicket ? 'Create Ticket' : 'Create Record'
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
